import { OverlayContainer } from '@angular/cdk/overlay';
import {
  Directive,
  computed,
  effect,
  input,
  output,
  signal,
} from '@angular/core';
import {
  outputToObservable,
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslocoService } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { assertExists } from 'common';
import { Field, FieldDataType, TextLayer, TextLayers } from 'models';
import { NGXLogger } from 'ngx-logger';
import { computedPrevious } from 'ngxtension/computed-previous';
import { createNotifier } from 'ngxtension/create-notifier';
import { derivedAsync } from 'ngxtension/derived-async';
import { derivedFrom } from 'ngxtension/derived-from';
import { explicitEffect } from 'ngxtension/explicit-effect';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  Subscription,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  finalize,
  interval,
  map,
  of,
  pairwise,
  pipe,
  shareReplay,
  startWith,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { TABLE_FIELD_TAB_INDEX } from '../components/document-view-sidebar/document-view-sidebar.component';
import {
  IndexerComponent,
  IndexerField,
} from '../components/indexer/indexer.component';
import { ProgressDialogComponent } from '../components/progress-dialog/progress-dialog.component';
import { TableFieldGridComponent } from '../components/table-field-grid/table-field-grid.component';
import { PdfViewerComponent } from '../modules/pdf-viewer';
import { KeyfreeIndexingService } from '../services/keyfree-indexing.service';
import { KfiEditDialogService } from '../services/kfi-edit-dialog.service';
import { NotificationService } from '../services/notification.service';
import { ProgressDialogService } from '../services/progress-dialog.service';
import { UiService } from '../services/ui.service';
import { ApplicationQuery } from '../state/application/application.query';

@UntilDestroy()
@Directive({
  selector: '[appKeyfree]',
  standalone: false,
})
export class KeyfreeDirective {
  /** Whether keyfree should be enabled. */
  enabled = input.required<boolean>({ alias: 'appKeyfree' });
  /** Whether OCR should be forced. */
  forceOcrSetting = input<boolean>(false, { alias: 'appKeyfreeForceOcr' });
  /** Whether OCR is enabled by temp need or enabled by user setting. */
  forceOcr = computed(() => this.tempForceOcr() || this.forceOcrSetting());
  /** Whether OCR should enable automatically when no text layer words are found. */
  autoOcr = input.required<boolean>({ alias: 'appKeyfreeAutoOcr' });
  /** Indexer reference. */
  indexer = input.required<IndexerComponent>({ alias: 'appKeyfreeIndexer' });
  /**
   * Emits when the last field useable by keyfree has been filled.
   *
   * This will **not** emit if there is a table field because keyfree will keep adding
   * new rows to the table field if on the last field.
   */
  lastFieldFilled = output({ alias: 'appKeyfreeLastFieldFilled' });
  /** PDF Viewer reference. */
  pdfViewer = input.required<PdfViewerComponent>({
    alias: 'appKeyfreePdfViewer',
  });
  /** Document view sidebar reference. */
  rightSidebar = computed(() => this.ui.state().documentViewerRightSidebar);
  /** Table field grid reference. */
  tableFieldGrid = computed(() => this.ui.state().tableField);

  /** KFI canvas element. */
  private annotationCanvas?: HTMLCanvasElement;
  /** KFI canvas context. */
  private annotationContext?: CanvasRenderingContext2D;
  private canvas?: HTMLCanvasElement;
  /** Currently selected field. */
  private currentField = derivedAsync(
    () => {
      const indexer = this.indexer();
      if (!this.enabled() || !indexer) return of(undefined);
      return indexer.fieldFocused.asObservable();
    },
    { initialValue: undefined }
  );
  /** Current hovered text layer. */
  private currentTextLayer: TextLayer;
  /** Drag text. */
  private dragText = '';
  /** Current page being viewed. */
  private pageNumber = derivedAsync(
    () => {
      const pdfViewer = this.pdfViewer();
      if (!pdfViewer) return of(1);
      return pdfViewer.pageChange.asObservable();
    },
    {
      initialValue: 1,
    }
  );
  /**
   * Holds the previous force ocr value for comparison during effects.
   *
   * This value will be the same as `forceOcr` if the value has not changed yet.
   */
  private previousForceOcrSetting = computedPrevious(this.forceOcrSetting);
  /**
   * Progress dialog reference for the load in progress dialog.
   *
   * This should be undefined if the dialog is not open.
   */
  private progressDialog:
    | MatDialogRef<ProgressDialogComponent, any>
    | undefined;
  /** Whether a drag is active on the KFI canvas. */
  private isDragActive = false;
  /** Whether mouse is down on the KFI canvas. */
  private isMouseDown = false;
  /** Whether shift is down on the KFI canvas. */
  private isShiftDown = false;
  /** Page currently being used by KFI. */
  private kfiPage = derivedAsync(
    () => {
      const pdfViewer = this.pdfViewer();
      if (!pdfViewer) return of(1);
      return pdfViewer.pageChange
        .asObservable()
        .pipe(debounceTime(1500), untilDestroyed(this), distinctUntilChanged());
    },
    {
      initialValue: 1,
    }
  );
  /** Track the kfi hover text when shift is held. */
  private kfiShiftHoverText = '';
  /** Track the kfi hover text. */
  private kfiHoverText = '';
  /** Object for tracking kfi rectangle. */
  private kfiRectangle = {
    x: 0,
    y: 0,
    startX: 0,
    startY: 0,
  };
  /** Rectangle div element. */
  private kfiRectangleDiv: HTMLDivElement;
  /**
   * Tracks the last focused field from the indexer.
   */
  private lastFocusedField = derivedAsync(() => {
    const indexer = this.indexer();
    if (!indexer) return undefined; // This shouldn't be possible but here we are getting undefined errors.
    // Observe the field focused and blurred events from the indexer so that we can tell when to refresh the last focused field value.
    // Use startWith to ensure ensure both start with an emission.
    return combineLatest([
      indexer.fieldFocused.pipe(startWith(undefined), untilDestroyed(this)),
      indexer.fieldBlurred.pipe(startWith(undefined)),
    ]).pipe(map(() => indexer.lastFocusedField));
  });
  /** Last page line focused. */
  private lastLine = 0;
  /** Redraw subscription. */
  private redrawSubscription: Subscription;
  private refreshKfi = createNotifier();
  /** Regular Expression highlight canvas. */
  private regexHighlightCanvas?: HTMLCanvasElement;
  /** Regular Expression highlight context. */
  private regexHighlightContext?: CanvasRenderingContext2D;
  /** Temporary forced OCR. */
  private tempForceOcr = signal(false);
  /** Text Layers. */
  private textLayers: TextLayers = [];
  /** Whether the use local OCR setting is enabled. */
  private useLocalOcr = toSignal(this.applicationQuery.keyfreeUseOcr$, {
    initialValue: false,
  });
  /** Current zoom scale of the viewer. */
  private zoomScale = derivedAsync(
    () => {
      const pdfViewer = this.pdfViewer();
      if (!pdfViewer) return of(1);
      return outputToObservable(pdfViewer.scaleChanged).pipe(
        debounceTime(500),
        untilDestroyed(this)
      );
    },
    { initialValue: 1 }
  );

  private updateTrigger$ = new BehaviorSubject<void>(undefined);
  private kfiUpdated$ = this.updateTrigger$.pipe(
    untilDestroyed(this),
    debounceTime(500),
    tap(() => {
      this.removeAnnotationCanvases();
      this.deleteAnnotationCanvas();
      this.removeRegExHighlightCanvases();
      this.ensureHoverElementsAreRemoved();
      this.addOrRemoveKfiOverlayClass('remove');
      this.createAnnotationCanvas();
      this.createRegexHighlightCanvas();
    }),
    switchMap(() =>
      this.createTextLayers().pipe(
        untilDestroyed(this),
        tap(() => {
          this.addOrRemoveKfiOverlayClass('add');
          this.redrawSubscription?.unsubscribe();
          this.redrawSubscription = interval(30)
            .pipe(untilDestroyed(this))
            .subscribe(() => {
              this.redrawKfi();
            });
        })
      )
    ),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  private kfiRenderEnabled = false;
  private textLayerCreationInProgress = false;

  constructor(
    private logger: NGXLogger,
    private applicationQuery: ApplicationQuery,
    private notify: NotificationService,
    private kfiEditDialogService: KfiEditDialogService,
    private progressDialogService: ProgressDialogService,
    private keyfreeService: KeyfreeIndexingService,
    private ui: UiService,
    private translate: TranslocoService,
    private overlayContainer: OverlayContainer
  ) {
    // These events must be bound so that 'this' in them is this class.
    this.keyDown = this.keyDown.bind(this);
    this.keyUp = this.keyUp.bind(this);

    let documentLoadingSubscription: Subscription | undefined = undefined;

    explicitEffect(
      [this.pdfViewer],
      ([pdfViewer]) => {
        pdfViewer.documentLoadingChange.asObservable().subscribe((loading) => {
          if (loading) {
            // Mark KFI as disabled when we load a new document, because enable will happen again.
            // This will help the other effect from mistriggering again for zoom etc while the load happens as well.
            this.kfiRenderEnabled = false;
            if (this.tempForceOcr())
              this.logger.debug(
                'Document loading, disabling KFI force OCR temp value.'
              );
            this.tempForceOcr.set(false);
          }
        });
      },
      { defer: false }
    );

    explicitEffect(
      [this.enabled, this.useLocalOcr],
      ([keyfreeEnabled, useLocalOcr]) => {
        documentLoadingSubscription?.unsubscribe();

        if (keyfreeEnabled) {
          this.logger.debug('Keyfree enabled.');
          this.enableKfi();
          documentLoadingSubscription = this.pdfViewer()
            .documentLoadingChange.asObservable()
            .pipe(pairwise(), debounceTime(500), untilDestroyed(this))
            .subscribe(([previousLoadingState, currentLoadingState]) => {
              if (!previousLoadingState && currentLoadingState) {
                // A document is being loaded so disable kfi
                this.disableKfi();
              } else if (previousLoadingState && !currentLoadingState) {
                this.enableKfi();
              }
            });
        } else {
          this.logger.debug('Keyfree disabled.');
          this.disableKfi();
        }
      },
      {
        defer: true,
      }
    );

    /** Create a signal that combines all signals for which we wish to refresh KFI. */
    const combinedUpdateSignal = derivedFrom(
      [
        this.kfiPage,
        toObservable(this.zoomScale).pipe(debounceTime(500)),
        this.forceOcrSetting,
        this.refreshKfi.listen,
      ],
      pipe(debounceTime(100)), // Prevent things like page scrolling from causing way to many updates.
      { initialValue: [1, 1, false, 0] }
    );

    /** Monitors the combination of signals that need to trigger a KFI updates. */
    explicitEffect(
      [combinedUpdateSignal],
      ([[kfiPage, zoomScale, forceOcrSetting, refreshKfi]]) => {
        if (this.kfiRenderEnabled === false) return;
        // handle when to just update the current kfi
        // Get enabled status without using it to trigger this effect.
        const enabled = this.enabled();
        // Don't update KFI if it is not enabled.
        if (!enabled) return;

        this.logger.debug('Loading for page: ', kfiPage);
        this.logger.debug(
          'Triggering update KFI for explicit effect on [kfiPage, zoomScale, forceOcrSetting, refreshKfi]',
          [kfiPage, zoomScale, forceOcrSetting, refreshKfi]
        );
        this.updateKfi().subscribe(() => {
          if (
            this.previousForceOcrSetting() !== this.forceOcrSetting() &&
            !this.previousForceOcrSetting() &&
            this.forceOcrSetting()
          ) {
            // Force OCR was just enabled so ensure we focus a field.
            this.setFieldFocus('first');
          }
        });
      },
      {
        defer: true,
      }
    );

    // Seperately watch for changes to the tempForceOcr to update KFI again only when it changes to true.
    explicitEffect(
      [this.tempForceOcr],
      ([tempForceOcr]) => {
        if (tempForceOcr) {
          const enabled = this.enabled();
          if (!enabled) return;
          this.logger.debug(
            'Triggering update KFI for Temporary force OCR enabled'
          );
          this.updateKfi().subscribe(() =>
            // Force OCR was just enabled so ensure we focus a field.
            this.setFieldFocus('first')
          );
        } else {
          this.logger.debug('Temporary force OCR disabled.');
        }
      },
      { defer: true }
    );

    /** Monitors the currently focused field to apply any regex highlights for KFI. */
    explicitEffect(
      [this.currentField],
      ([currentField]) => {
        const enabled = this.enabled();
        if (!enabled || !currentField) return;

        this.highlightByRegularExpression(currentField);
      },
      {
        defer: true,
      }
    );
  }

  private addOrRemoveKfiOverlayClass(classChange: 'add' | 'remove'): void {
    try {
      const overlayContainerElement =
        this.overlayContainer.getContainerElement();
      switch (classChange) {
        case 'add':
          overlayContainerElement.classList.add('kfi-active');
          break;
        case 'remove':
          overlayContainerElement.classList.remove('kfi-active');
          break;
      }
    } catch (error) {
      this.logger.debug('Error adding or removing kfi-active class: ', error);
    }
  }

  private clearRegExHighlights() {
    if (!this.regexHighlightContext || !this.canvas) return;
    for (const textLayer of this.textLayers) {
      textLayer.regExHighlighted = false;
    }
    this.regexHighlightContext.clearRect(
      0,
      0,
      this.canvas.clientWidth,
      this.canvas.clientHeight
    );
  }

  /**
   * Create the canvas in to draw kfi annotations on.
   *
   */
  private createAnnotationCanvas() {
    try {
      const pageElement = this.pdfViewer().pageElement;
      this.annotationCanvas = document.createElement('canvas');
      this.annotationCanvas.id = 'anno_page_' + this.pageNumber();
      this.canvas = pageElement.querySelector(
        '.canvasWrapper [role="presentation"]'
      ) as HTMLCanvasElement;
      if (!this.canvas) return;
      this.annotationCanvas.height = this.canvas.height || 0;
      this.annotationCanvas.width = this.canvas.width || 0;
      this.annotationCanvas.style.position = 'absolute';
      this.annotationCanvas.style.top = '0px';
      this.annotationCanvas.style.left = '0px';
      this.annotationCanvas.style.zIndex = '100';
      // Style height and width are needed for dealing with device pixel ratio.
      this.annotationCanvas.style.width = '100%';
      this.annotationCanvas.style.height = '100%';
      pageElement.append(this.annotationCanvas);
      this.annotationContext = this.annotationCanvas.getContext(
        '2d'
      ) as CanvasRenderingContext2D;

      // Scale the canvas to match device pixel ratio.
      this.annotationContext.scale(
        window.devicePixelRatio,
        window.devicePixelRatio
      );

      this.annotationCanvas.addEventListener('mouseup', this.mouseUp);
      this.annotationCanvas.addEventListener('mousedown', this.mouseDown);
      this.annotationCanvas.addEventListener('mousemove', this.kfiMouseMove);
      this.annotationCanvas.addEventListener(
        'contextmenu',
        this.kfiContextMenu
      );
      document.addEventListener('keydown', this.keyDown);
      document.addEventListener('keyup', this.keyUp);
    } catch (error) {
      this.logger.debug('Error creating annotation canvas: ', error);
    }
  }

  /**
   * Creates the html for the floating div that shows the user the text and selected field.
   *
   * @returns - Hovertext element.
   */
  private createHoverText(): HTMLDivElement {
    const pageElement = this.pdfViewer().pageElement;
    const hoverText = document.createElement('div');
    hoverText.id = 'kfi_hover_text';
    hoverText.style.borderRadius = '5px';
    hoverText.style.boxShadow = '0px 3px 5px lightgray';
    //Create plus icon for shift key press
    const plusDiv = document.createElement('div');
    plusDiv.id = 'kfi_hover_plus';
    plusDiv.style.width = '18px';
    plusDiv.style.height = '18px';
    plusDiv.style.borderRadius = '50%';
    plusDiv.style.backgroundColor = 'rgb(96, 125, 139)';
    plusDiv.style.float = 'left';
    plusDiv.style.position = 'absolute';
    plusDiv.style.top = '-9px';
    plusDiv.style.left = '-9px';
    plusDiv.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" height="18px" viewBox="0 -960 960 960" width="18px" fill="#FFFFFF"><path d="M440-440H200v-80h240v-240h80v240h240v80H520v240h-80v-240Z"/></svg>';
    plusDiv.hidden = true;
    hoverText.append(plusDiv);
    //Create header within hovertext div for field name
    const header = document.createElement('div');
    header.id = 'hover_header';
    header.style.color = 'white';
    header.style.minWidth = '100px';
    header.style.backgroundColor = '#607d8b';
    header.style.padding = '5px';
    header.style.borderRadius = '5px 5px 0 0';
    hoverText.append(header);
    //Create body withing hovertext div for field value(s)
    const body = document.createElement('div');
    body.id = 'hover_content';
    body.style.padding = '5px';
    body.style.whiteSpace = 'pre-line';
    hoverText.append(body);
    pageElement.prepend(hoverText);
    return hoverText;
  }

  private createKfiDragRectangle(event: MouseEvent) {
    assertExists(this.annotationCanvas, 'Annotation canvas must exist.');
    const rect = this.annotationCanvas.getBoundingClientRect(),
      x = event.clientX - rect.left,
      y = event.clientY - rect.top;
    const context = this.annotationCanvas.getContext(
      '2d'
    ) as CanvasRenderingContext2D;
    if (
      !context.isPointInPath(x, y) &&
      !this.textLayers.some((layer) => layer.highlighted)
    ) {
      this.isDragActive = true;
      this.kfiRectangle.startX = x;
      this.kfiRectangle.startY = y;
      this.kfiRectangleDiv = document.createElement('div') as HTMLDivElement;
      this.kfiRectangleDiv.className = 'kfi-rectangle';
      this.kfiRectangleDiv.style.left = x + 'px';
      this.kfiRectangleDiv.style.top = y + 'px';
      this.kfiRectangleDiv.style.border = '2px solid rgb(0,0,255, .2)';
      this.kfiRectangleDiv.style.position = 'absolute';
      this.pdfViewer().pageElement.append(this.kfiRectangleDiv);
    }
  }

  /**
   * Create the elements needed for regular expression highlights.
   */
  private createRegexHighlightCanvas() {
    if (!this.canvas) return;
    this.regexHighlightCanvas = document.createElement('canvas');
    this.regexHighlightCanvas.id = 'regex_highlight_page_' + this.pageNumber();
    this.regexHighlightCanvas.height = this.canvas.height || 0;
    this.regexHighlightCanvas.width = this.canvas.width || 0;
    this.regexHighlightCanvas.style.position = 'absolute';
    this.regexHighlightCanvas.style.top = '0px';
    this.regexHighlightCanvas.style.left = '0px';
    this.regexHighlightCanvas.style.zIndex = '99';
    // Style height and width are needed for dealing with device pixel ratio.
    this.regexHighlightCanvas.style.width = '100%';
    this.regexHighlightCanvas.style.height = '100%';
    this.pdfViewer().pageElement.append(this.regexHighlightCanvas);
    this.regexHighlightContext = this.regexHighlightCanvas.getContext(
      '2d'
    ) as CanvasRenderingContext2D;

    // Scale the canvas to match device pixel ratio.
    this.regexHighlightContext.scale(
      window.devicePixelRatio,
      window.devicePixelRatio
    );
  }

  private createTextLayers(): Observable<void> {
    if (this.textLayerCreationInProgress) return EMPTY;
    this.textLayerCreationInProgress = true;
    return of(null).pipe(
      switchMap(() => {
        this.textLayers.length = 0;
        if (this.useLocalOcr() || this.forceOcr()) {
          const lastFocusedField = this.lastFocusedField();
          // Tesseract
          const pageElement = this.pdfViewer().pageElement.querySelector(
            'canvas'
          ) as HTMLCanvasElement;
          if (!pageElement) return EMPTY;
          this.progressDialog = this.progressDialogService.openProgressDialog(
            'LOADING_KEYFREE_OCR_PAGE',
            {
              pageNumber: this.pageNumber().toString(),
            }
          );
          return this.keyfreeService.getWords(pageElement).pipe(
            untilDestroyed(this),
            map((page: Tesseract.Page) => {
              assertExists(
                page.blocks,
                'Tesseract blocks data required for parsing bounding boxes.'
              );
              const lines = page.blocks
                .map((block) =>
                  block.paragraphs.map((paragraph) => paragraph.lines)
                )
                .flat(2);
              for (const [lineIndex, line] of lines.entries()) {
                for (const word of line.words) {
                  this.textLayers.push({
                    height:
                      (word.bbox.y1 - word.bbox.y0) / window.devicePixelRatio,
                    width:
                      (word.bbox.x1 - word.bbox.x0) / window.devicePixelRatio,
                    text: word.text,
                    words: [word.text], // Should only be one layer per word with this logic.
                    multiSelected: false,
                    line: lineIndex,
                    x: word.bbox.x0 / window.devicePixelRatio,
                    y: word.bbox.y0 / window.devicePixelRatio,
                    regExHighlighted: false,
                    dragSelect: false,
                    highlighted: false,
                    selected: false,
                  });
                }
              }
              this.indexer().lastFocusedField = lastFocusedField;
              return; // Done, return void from the chain.
            })
          );
        } else {
          assertExists(this.annotationCanvas);
          // Existing Pdf text layer
          const words = this.pdfViewer().getPageWords(
            this.pageNumber(),
            this.annotationCanvas.width
          );
          let lineIndex = 0,
            previousWordHeight = 0,
            previousWordTop = 0;
          return words.pipe(
            untilDestroyed(this),
            switchMap((words) => {
              // If page contains no words, re-run with OCR.
              if (this.autoOcr() && words.length === 0) {
                this.notify.info('PAGE_HAS_NO_TEXT_USING_OCR');
                this.tempForceOcr.set(true);
                return EMPTY;
              }
              // Otherwise continue as normal.
              return of(words);
            }),
            map((words) => words.sort((a: any, b: any) => a.y - b.y)),
            // Calculate line numbers
            map((words: any) =>
              words.map((word: any) => {
                const wordIsAlmostSameHeight =
                  Math.abs(word.height - previousWordHeight) <
                  0.1 * previousWordHeight;

                // Check if the word is adjacent to the previous word.
                const wordIsAdjacentWithinPreviousHeight =
                  word.y <= previousWordTop + previousWordHeight;

                // If the words are not roughly the same line height, or not adjacent to the previous word, start a new line.
                if (
                  !wordIsAlmostSameHeight ||
                  !wordIsAdjacentWithinPreviousHeight
                )
                  lineIndex++;

                // Set the word's line number.
                word.line = lineIndex;

                // Update the previous word values.
                previousWordHeight = word.height;
                previousWordTop = word.y;

                // Return the modified word.
                return word;
              })
            ),
            // Sort by line number, and ascending x coordinates where line number is the the same
            map((results) =>
              results.sort((a: any, b: any) => a.line - b.line || a.x - b.x)
            ),
            map((words) => {
              this.textLayers = words.map((word: any) => ({
                height: word.height / window.devicePixelRatio,
                width: word.width / window.devicePixelRatio,
                text: word.text,
                line: word.line,
                words: [],
                x: word.x / window.devicePixelRatio,
                y: word.y / window.devicePixelRatio,
                dragSelect: false,
                regExHighlighted: false,
                highlighted: false,
                selected: false,
                multiSelected: false,
              }));
              return; // Done, return void from the chain.
            })
          );
        }
      }),
      finalize(() => {
        this.textLayerCreationInProgress = false;
        if (this.progressDialog) {
          this.progressDialog.close();
          this.progressDialog = undefined;
        }
      })
    );
  }

  private deleteAnnotationCanvas() {
    if (!this.annotationCanvas) return;
    this.annotationCanvas.removeEventListener('mousemove', this.kfiMouseMove);
    this.annotationCanvas.removeEventListener('mouseup', this.mouseUp);
    this.annotationCanvas.removeEventListener('mousedown', this.mouseDown);
    this.annotationCanvas.removeEventListener(
      'contextmenu',
      this.kfiContextMenu
    );
    document.removeEventListener('keydown', this.keyDown);
    document.removeEventListener('keyup', this.keyUp);
    this.annotationCanvas.remove();
  }

  private disableKfi(): void {
    this.addOrRemoveKfiOverlayClass('remove');
    // TODO address the scale change
    // Ensure the scale change subscription is unsubscribed if it exists.
    // this.scaleChangeSubscription?.unsubscribe();
    this.indexer().lastFocusedField = undefined;
    this.hideHoverText();
    this.redrawSubscription?.unsubscribe();
    this.removeAnnotationCanvases();
    this.deleteAnnotationCanvas();
    this.removeRegExHighlightCanvases();
    this.ensureHoverElementsAreRemoved();
    this.kfiRenderEnabled = false;
  }

  private enableKfi(): void {
    // Prevent multiple calls to enableKfi from creating multiple intervals.
    if (this.kfiRenderEnabled) return;
    this.kfiRenderEnabled = true;
    this.addOrRemoveKfiOverlayClass('add');
    this.logger.debug('Triggering update KFI for enableKfi');
    this.updateKfi()
      .pipe(
        take(1), // We only want the initial emission from update kfi here to avoid continually resetting the field focus on every update.
        finalize(() => this.setFieldFocus('first')) // We always want to focus the first field regardless of if an error emits from updateKfi.
      )
      .subscribe();
  }

  private ensureHoverElementsAreRemoved() {
    try {
      const pageElement = this.pdfViewer().pageElement;
      const hoverText = pageElement.querySelector(
        '#kfi_hover_text'
      ) as HTMLElement;
      if (!hoverText) return;
      hoverText.remove();
      for (const element of this.pdfViewer().pageElement.querySelectorAll(
        '.kfi-rectangle'
      )) {
        element.remove();
      }
    } catch (error) {
      this.logger.debug('Error removing hover elements: ', error);
    }
  }

  // We need to know the locales decimal seperator character (can't just assume .)
  private getDecimalSeparator(locale: any) {
    const numberWithDecimalSeparator = 1.1;
    const numberFormat = Intl.NumberFormat(locale);
    assertExists(numberFormat, 'Number format must exist.');
    const formatParts = numberFormat.formatToParts(numberWithDecimalSeparator);
    assertExists(formatParts, 'Must be able to format to parts.');
    return formatParts.find((part) => part.type === 'decimal')?.value;
  }

  private getNextField(firstField = false) {
    const currentField = firstField ? undefined : this.lastFocusedField(),
      indexerFields = this.indexer().indexerFields;
    let index = currentField
      ? indexerFields.findIndex((f) => f.id == currentField.id) + 1
      : 0;
    let field: IndexerField | undefined = this.indexer().indexerFields[index];
    if (!field && index >= this.indexer().indexerFields.length) {
      // No more fields to iterate through.
      // Field will be undefined at this point.
      return undefined;
    }
    let fieldControl = this.indexer().getFormControl(field.id);

    // Skip any field disabled in the indexer form.
    while (field && fieldControl.disabled) {
      index++;
      field = this.indexer().indexerFields[index];
      if (!field && index >= this.indexer().indexerFields.length) {
        // No more fields to iterate through.
        // Field will be undefined at this point.
        break;
      }
      fieldControl = this.indexer().getFormControl(field.id);
    }

    return field;
  }

  private hideHoverPlus(): void {
    const hoverPlus = this.pdfViewer().pageElement.querySelector(
      '#kfi_hover_plus'
    ) as HTMLElement;
    if (!hoverPlus) return;
    hoverPlus.hidden = true;
  }

  private hideHoverText(): void {
    const hoverText = this.pdfViewer().pageElement.querySelector(
      '#kfi_hover_text'
    ) as HTMLElement;
    if (!hoverText) return;
    this.kfiHoverText = '';
    hoverText.hidden = true;
  }

  private highlightByRegularExpression(field: Field): void {
    if (!this.regexHighlightContext || !this.canvas) {
      this.logger.warn(
        'The highlight by regex was called when either the highlight context or canvas did not exist.'
      );
      return;
    }
    this.regexHighlightContext.clearRect(
      0,
      0,
      this.canvas.width,
      this.canvas.height
    );
    if (field.regex) {
      // Remove highlight when clicking also check existing functionality
      this.regexHighlightContext.beginPath();
      for (const textLayer of this.textLayers) {
        const expression = new RegExp(field.regex.replace(/\(\?#.*? #\)/g, ''));
        if (expression.test(textLayer.text)) {
          textLayer.regExHighlighted = true;
          this.regexHighlightContext.rect(
            textLayer.x,
            textLayer.y,
            textLayer.width,
            textLayer.height
          );
          this.regexHighlightContext.fillStyle = 'rgba(0,255,0,.2)';
          this.regexHighlightContext.fillRect(
            textLayer.x,
            textLayer.y,
            textLayer.width,
            textLayer.height
          );
        }
      }
    }
  }

  /**
   * Returns if the drag select should highlight a word.
   *
   * @param textLayer - Text layer object.
   * @returns - Boolean.
   */
  private shouldKfiDragHighlight(textLayer: TextLayer): boolean {
    if (!this.kfiRectangleDiv) return false;
    const boxLeft = Number.parseInt(this.kfiRectangleDiv.style.left, 10),
      boxTop = Number.parseInt(this.kfiRectangleDiv.style.top, 10),
      boxRight =
        Number.parseInt(this.kfiRectangleDiv.style.left, 10) +
        Number.parseInt(this.kfiRectangleDiv.style.width, 10),
      boxBottom =
        Number.parseInt(this.kfiRectangleDiv.style.top, 10) +
        Number.parseInt(this.kfiRectangleDiv.style.height, 10);

    // Text layer falls within the bounds of the box.
    const textLayerInBox =
      textLayer.x > boxLeft &&
      textLayer.x + textLayer.width < boxRight &&
      textLayer.y > boxTop &&
      textLayer.y + textLayer.height < boxBottom;

    // // Text layer is overlapped by the box on the x-axis.
    const textLayerOverlapX =
      textLayer.x <= boxRight && textLayer.x + textLayer.width >= boxLeft;

    // Text layer is overlapped by the box on the y-axis.
    const textLayerOverlapY =
      textLayer.y <= boxBottom && textLayer.y + textLayer.height >= boxTop;

    return textLayerInBox || (textLayerOverlapX && textLayerOverlapY);
  }

  private initializeKfiRectangleDiv(x: number, y: number): void {
    this.kfiRectangleDiv.style.width =
      Math.abs(x - this.kfiRectangle.startX) + 'px';
    this.kfiRectangleDiv.style.height =
      Math.abs(y - this.kfiRectangle.startY) + 'px';
    this.kfiRectangleDiv.style.left =
      x - this.kfiRectangle.startX < 0
        ? x + 'px'
        : this.kfiRectangle.startX + 'px';
    this.kfiRectangleDiv.style.top =
      y - this.kfiRectangle.startY < 0
        ? y + 'px'
        : this.kfiRectangle.startY + 'px';
  }

  private keyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Shift') {
      this.showHoverPlus();
    }
  };

  private keyUp = (event: KeyboardEvent): void => {
    if (event.key === 'Alt') {
      event.preventDefault();
    }
    if (event.key === 'Shift') {
      this.hideHoverPlus();
      if (!this.currentTextLayer) return;
      for (const textLayer of this.textLayers) {
        textLayer.highlighted = false;
        textLayer.multiSelected = false;
      }
      if (this.kfiShiftHoverText) {
        this.setNextFocusedFieldValue(this.kfiShiftHoverText);
        this.kfiShiftHoverText = '';
      }
    }
  };

  /**
   * Kfi mouse click event handler.
   *
   * @param event - Mouse Event.
   */
  kfiContextMenu = (event: MouseEvent) => {
    // Prevent the default context menu from showing up on right click.
    event.preventDefault();
  };

  /**
   * Kfi mouse movement event.
   *
   * @param event - Mouse Event.
   */
  kfiMouseMove = (event: MouseEvent) => {
    if (!this.canvas) return;
    const rect = this.canvas.getBoundingClientRect(),
      x = event.clientX - rect.left,
      y = event.clientY - rect.top;

    this.isShiftDown = event.shiftKey;

    if (this.kfiRectangleDiv && this.isMouseDown && this.isDragActive) {
      this.initializeKfiRectangleDiv(x, y);
    }

    const context = this.canvas.getContext('2d') as CanvasRenderingContext2D;

    for (const textLayer of this.textLayers) {
      // Add a single rect to path:
      context.beginPath();
      context.rect(textLayer.x, textLayer.y, textLayer.width, textLayer.height);

      if (
        context.isPointInPath(x, y) &&
        !textLayer.highlighted &&
        !this.isDragActive
      ) {
        textLayer.highlighted = true;
        this.currentTextLayer = textLayer;
        this.updateHoverTextLocation(x, y);
        textLayer.selected = true;
        if (this.isMouseDown || this.isShiftDown) {
          this.updateHoverText(textLayer.text, textLayer.line);
        }
      } else if (
        !event.shiftKey &&
        !context.isPointInPath(x, y) &&
        !this.isMouseDown &&
        !this.isDragActive
      ) {
        textLayer.highlighted = false;
      } else if (
        event.shiftKey &&
        !context.isPointInPath(x, y) &&
        !textLayer.multiSelected &&
        !this.isDragActive
      ) {
        this.kfiShiftHoverText = '';
        textLayer.highlighted = false;
      } else if (
        !textLayer.dragSelect &&
        this.isDragActive &&
        this.shouldKfiDragHighlight(textLayer)
      ) {
        textLayer.dragSelect = true;
        this.updateHoverTextLocation(x, y);
      } else if (
        textLayer.dragSelect &&
        !this.shouldKfiDragHighlight(textLayer)
      ) {
        textLayer.dragSelect = false;
        textLayer.highlighted = false;
      }
    }
  };

  private mouseDown = (event: MouseEvent) => {
    event.preventDefault();
    this.isMouseDown = true;
    this.createKfiDragRectangle(event);
  };

  /**
   * Mouse click event.
   *
   * @param event - Mouse Event.
   */
  private mouseUp = (event: MouseEvent) => {
    assertExists(this.annotationCanvas, 'Annotation canvas must exist.');
    this.isMouseDown = false;
    this.clearRegExHighlights();
    const isRightClick = event.button === 2;
    const rect = this.annotationCanvas.getBoundingClientRect(),
      x = event.clientX - rect.left,
      y = event.clientY - rect.top;
    if (this.currentTextLayer && !this.isDragActive) {
      if (!event.shiftKey) {
        if (event.altKey) {
          const currentField = this.currentField();
          assertExists(currentField);
          this.kfiEditDialogService
            .openKfiEditDialog(currentField.name, this.dragText)
            .subscribe((result) => {
              if (result) {
                this.setNextFocusedFieldValue(result);
              } else {
                this.setFieldFocus(false);
              }
            });
        } else {
          this.setNextFocusedFieldValue(this.dragText, !isRightClick);
        }
      }
      if (event.shiftKey && !this.currentTextLayer.multiSelected) {
        this.currentTextLayer.multiSelected = true;
      }
    }
    if (
      this.kfiRectangleDiv &&
      Number.parseInt(this.kfiRectangleDiv.style.width, 10) > 2 &&
      this.isDragActive
    ) {
      if (event.altKey) {
        const currentField = this.currentField();
        assertExists(currentField);
        this.kfiEditDialogService
          .openKfiEditDialog(currentField.name, this.dragText)
          .subscribe((result) => {
            if (result) {
              this.setNextFocusedFieldValue(result);
            } else {
              this.setFieldFocus(false);
            }
          });
      } else {
        this.setNextFocusedFieldValue(this.dragText);
      }
    }
    this.isDragActive = false;
    for (const element of this.pdfViewer().pageElement.querySelectorAll(
      '.kfi-rectangle'
    )) {
      element.remove();
    }
  };

  private redrawKfi = () => {
    // Page not ready yet, abort. Next cycle of the calling timeout will continue checks.
    try {
      this.pdfViewer().pageElement;
    } catch {
      return;
    }
    // Never set anything here, only draw
    if (!this.canvas || !this.annotationContext) return;
    this.annotationContext.clearRect(
      0,
      0,
      this.canvas.clientWidth,
      this.canvas.clientHeight
    );
    let highlightsExist = false;

    for (const textLayer of this.textLayers) {
      // Not using drag select
      if (textLayer.highlighted && !this.isDragActive) {
        // Only highlight words that are not already reg ex highlighted.
        if (!textLayer.regExHighlighted) {
          this.annotationContext.beginPath();
          this.annotationContext.rect(
            textLayer.x,
            textLayer.y,
            textLayer.width,
            textLayer.height
          );

          this.annotationContext.fillStyle = 'rgba(255,0,0,.2)';
          if (textLayer.multiSelected) {
            this.annotationContext.fillStyle = 'rgb(0,0,255,.2)';
          }
          this.annotationContext.fillRect(
            textLayer.x,
            textLayer.y,
            textLayer.width,
            textLayer.height
          );
        }
        highlightsExist = true;
        if (!this.isMouseDown) {
          this.updateHoverText(textLayer.text, textLayer.line);
        }
      }
      // Using drag select.
      if (this.isDragActive && textLayer.dragSelect) {
        this.annotationContext.beginPath();
        this.annotationContext.rect(
          textLayer.x,
          textLayer.y,
          textLayer.width,
          textLayer.height
        );
        this.annotationContext.fillStyle = 'rgb(0,0,255,.2)';
        this.annotationContext.fillRect(
          textLayer.x,
          textLayer.y,
          textLayer.width,
          textLayer.height
        );
        this.updateHoverText(textLayer.text, textLayer.line);
        textLayer.highlighted = true;
        highlightsExist = true;
      }
    }
    this.dragText =
      this.kfiHoverText !== '' ? this.kfiHoverText : this.dragText;

    if ((this.isMouseDown && this.isDragActive) || this.isShiftDown) {
      this.kfiHoverText = '';
    }

    if (!this.isDragActive) {
      for (const textLayer of this.textLayers) {
        if (textLayer.dragSelect) {
          textLayer.dragSelect = false;
        }
      }
    }
    if (!highlightsExist) {
      this.hideHoverText();
    }
    if (!this.lastFocusedField()) {
      const targetField = this.tableFieldGrid()?.getTargetField();
      if (targetField) {
        this.updateHoverHeader(targetField?.name);
      } else {
        this.updateHoverHeader(this.translate.translate('NO_FIELD_SELECTED'));
      }
    }
  };

  /**
   * Removes every instance of the annotation canvases used for kfi.
   */
  private removeAnnotationCanvases(): void {
    try {
      const elements =
        this.pdfViewer().contentWindow.document.querySelectorAll(
          "[id^='anno_page_']"
        );
      for (const element of elements) element.remove();
    } catch (error) {
      this.logger.debug('Error removing annotation canvases, error:', error);
    }
  }

  /**
   * Removes every instance of the regular expression canvases used for kfi.
   */
  private removeRegExHighlightCanvases(): void {
    try {
      const elements = this.pdfViewer().contentWindow.document.querySelectorAll(
        "[id^='regex_highlight_page_']"
      );
      for (const element of elements) element.remove();

      if (this.regexHighlightCanvas) {
        this.regexHighlightCanvas.remove();
      }
    } catch (error) {
      this.logger.debug(
        'Error removing regex highlight canvases, error:',
        error
      );
    }
  }

  private setFieldFocus(nextField?: boolean | 'first'): Field {
    const field = nextField
      ? this.getNextField(nextField === 'first')
      : this.lastFocusedField() || this.currentField() || this.getNextField();
    assertExists(field, 'Field must exist');
    if (field.multiValue) {
      const component = this.indexer().multiValueFieldComponents.find(
        (f) => f.field.id === field.id
      );
      assertExists(
        component,
        'MultiValue Field component for the field must exist.'
      );
      // There should always be at least one MV field entry.
      component.fieldComponents.first.fieldComponent.focus();
    } else {
      const component = this.indexer().fieldComponents.find(
        (f) => f.field.id === field.id
      );
      assertExists(component, 'Field component for the field must exist.');
      component.fieldComponent.focus();
    }
    this.updateHoverHeader(field.name);
    return field;
  }

  private setNextFocusedFieldValue(text: string, goToNextField = true): void {
    if (!this.lastFocusedField()) {
      // If the last focused field is undefined we've exhausted fields and move on to table fields
      if (this.indexer().tableFields.length > 0) {
        const targetField = this.tableFieldGrid()?.getTargetField();
        assertExists(targetField, 'Table field must exist.');
        text = this.validateFieldValue(targetField, text);
        if (text.includes('\n')) {
          this.tableFieldGrid()?.addColumnData(text.split('\n'));
        } else {
          this.tableFieldGrid()?.addDataToFocusedCell(text, true);
        }
        this.lastLine = 0;
        this.updateHoverHeader(targetField.name);
      }
    } else {
      // Set last focused field to a variable and assert to make type safety happy
      // The property can't be null here because the above if statement handles it.
      const lastFocusedField = this.lastFocusedField();
      assertExists(lastFocusedField, 'Last focused field must exist.');
      if (!lastFocusedField) return;
      for (
        let index = 0, c = this.indexer().indexerFields.length;
        index < c;
        ++index
      ) {
        if (lastFocusedField.id === this.indexer().indexerFields[index].id) {
          text = this.validateFieldValue(
            this.indexer().indexerFields[index],
            text
          );
          // Multi Value Field
          if (this.indexer().indexerFields[index].multiValue) {
            this.indexer().addValueToMultiValueField(
              this.indexer().indexerFields[index].id,
              [text]
            );
            if (!goToNextField) {
              const mvFieldComponent =
                this.indexer().multiValueFieldComponents.find(
                  (c) => c.field.id === this.indexer().indexerFields[index].id
                );
              setTimeout(() => {
                mvFieldComponent?.fieldComponents.last.fieldComponent.focus();
              });
            }
          } else {
            if (!text) {
              this.logger.warn('No text available in selection.');
              return;
            }
            // Regular Field
            this.indexer().setFieldValue(
              this.indexer().indexerFields[index].id,
              text.replace(/[\n\r]/g, ' ')
            );
          }
          // Add one to set it to the next field.
          const nextField = this.getNextField();

          if (this.tableFieldGrid()?.activeTableField && !nextField) {
            this.setNextTableFieldFocused();
            return;
          }

          // If empty clear kfi annotations and turn off kfi.
          if (!nextField && this.annotationCanvas && this.annotationContext) {
            this.annotationContext.clearRect(
              0,
              0,
              this.annotationCanvas.clientWidth,
              this.annotationCanvas.clientHeight
            );
            this.toggleKfi();
            this.lastFieldFilled.emit();
            return;
          }
          this.setFieldFocus(goToNextField);
          return;
        }
      }
    }
  }

  private setNextTableFieldFocused(): void {
    const tableFieldGrid = this.tableFieldGrid();
    if (
      this.applicationQuery.viewerOpenTableFieldsInSidebar &&
      tableFieldGrid
    ) {
      this.logger.debug(
        'Table fields are set to open in the sidebar. Opening the tab in the sidebar.'
      );
      this.rightSidebar()?.selectTab(TABLE_FIELD_TAB_INDEX);
    }
    // Focus table field inputs.
    const columns = tableFieldGrid?.grid.api.getAllGridColumns() || [];
    if (tableFieldGrid?.activeTableField) {
      let lastRowIndex = tableFieldGrid.grid.api.getLastDisplayedRowIndex();
      if (lastRowIndex === -1) {
        // There are no rows if we get here so add one and try to get the index again.
        tableFieldGrid.onClickAddRow();
        lastRowIndex = tableFieldGrid.grid.api.getLastDisplayedRowIndex();
      }
      const row = tableFieldGrid.grid.api.getDisplayedRowAtIndex(lastRowIndex);
      assertExists(row, 'A row must exist.');
      // Check to see if we should add a new row.
      let valuesExist = false;
      for (const [key, value] of Object.entries(row.data)) {
        if (value !== '' && key !== 'id') valuesExist = true;
      }
      if (valuesExist) {
        tableFieldGrid.onClickAddRow();
        lastRowIndex = tableFieldGrid.grid.api.getLastDisplayedRowIndex();
      }
      // We need to ignore the selection column when determining the column to focus.
      const firstFocusableColumn = columns.find(
        (c) => c.getColId() !== 'ag-Grid-SelectionColumn'
      );
      assertExists(
        firstFocusableColumn,
        'There must be at least one focusable column.'
      );
      tableFieldGrid?.focusCellForEditing(lastRowIndex, firstFocusableColumn);
      const targetedField = tableFieldGrid?.getTargetField();
      if (!targetedField) return;
      this.updateHoverHeader(targetedField.name);
    }
  }

  private showHoverPlus() {
    const hoverPlus = this.pdfViewer().pageElement.querySelector(
      '#kfi_hover_plus'
    ) as HTMLElement;
    if (!hoverPlus) return;
    hoverPlus.hidden = false;
  }

  private toggleKfi(): void {
    const forceOcr = this.forceOcr();
    const useLocalOcr = this.useLocalOcr();
    if (forceOcr && !useLocalOcr) {
      this.removeAnnotationCanvases();
      this.removeRegExHighlightCanvases();
    }
  }

  private updateHoverHeader(value: string) {
    if (!value) return;
    const hoverHeader = this.pdfViewer().pageElement.querySelector(
      '#hover_header'
    ) as HTMLElement;
    if (!hoverHeader) return;
    hoverHeader.textContent = value;
  }

  private updateHoverText(word: string, line: number): void {
    const hoverText = this.pdfViewer().pageElement.querySelector(
      '#kfi_hover_text'
    ) as HTMLElement;
    if (hoverText) hoverText.hidden = false;
    const header = this.lastFocusedField()?.name ?? '';
    this.updateHoverHeader(header);
    if (!this.isMouseDown && !this.isShiftDown) {
      this.kfiHoverText = word;
      this.lastLine = line;
    } else if (!this.isMouseDown && this.isShiftDown) {
      let separator = ' ';
      if (this.lastLine === 0) {
        this.lastLine = line;
      }
      if (this.lastLine !== line) {
        separator = '\n';
      }
      this.lastLine = line;
      this.kfiHoverText =
        this.kfiHoverText.trim() === ''
          ? word
          : this.kfiHoverText + separator + word;
      this.kfiShiftHoverText = this.kfiHoverText;
    } else if (!this.isShiftDown) {
      let separator = ' ';
      if (this.lastLine === 0) {
        this.lastLine = line;
      }
      if (this.lastLine !== line && !this.isShiftDown) {
        separator = '\n';
      }
      this.lastLine = line;
      this.kfiHoverText =
        this.kfiHoverText.trim() === ''
          ? word
          : this.kfiHoverText + separator + word;
    }
    this.updateHoverContent();
  }

  private updateHoverContent(): void {
    const pageElement = this.pdfViewer().pageElement;
    (pageElement.querySelector('#hover_content') as HTMLElement).textContent =
      this.kfiHoverText;
  }

  private updateHoverTextLocation(x: number, y: number): void {
    assertExists(this.annotationCanvas, 'Annotation canvas must exist.');
    let hoverText = this.pdfViewer().pageElement.querySelector(
      '#kfi_hover_text'
    ) as HTMLElement;
    if (!hoverText) {
      hoverText = this.createHoverText();
    }
    hoverText.style.zIndex = '9999999';
    hoverText.style.left =
      // Keep the hover text on the screen and not behind the indexer.
      this.annotationCanvas.width - (x + 150) < 0
        ? (x - 125).toString() + 'px'
        : (x + 25).toString() + 'px';
    hoverText.style.top =
      this.annotationCanvas.height - (y + 150) < 0
        ? (y - 125).toString() + 'px'
        : (y + 25).toString() + 'px';
    hoverText.style.backgroundColor = 'white';
    hoverText.style.position = 'absolute';
    hoverText.hidden = false;
  }

  private updateKfi(): Observable<void> {
    if (!this.enabled()) return of();
    this.updateTrigger$.next();
    return this.kfiUpdated$;
  }

  /**
   * Checks to see if the value that will be passed matches the data type of the field.
   *
   * @param field - Field.
   * @param value - Value to be passed into the field.
   * @returns - The value if it's valid or '' if not.
   */
  private validateFieldValue(field: Field, value: any): any {
    if (!value) return;
    // Get the current locale from the browser (this could just be in the function if we only ever support current)
    const locale = navigator.languages[0];
    // Checking for a negative marker in the string (this is the biggest part of it that is guessing)
    const hasNeg = value.startsWith('-');
    const separator = this.getDecimalSeparator(locale);
    // Remove anything that is not a number or separator
    const re = new RegExp('[^\\d' + separator + ']', 'g');
    let sanitizedNumber = value.replace(re, '');
    // Remark as negative if needed
    sanitizedNumber = hasNeg ? '-' + sanitizedNumber : sanitizedNumber;
    // Get the final value as a number, and if a number
    const asNumber = Number.parseFloat(sanitizedNumber);
    const isNumber = !Number.isNaN(asNumber);
    // The number as a string does not match the sanitized number string it came from
    // so it could be something like where sanitized value is '1.2.3.4' in which case the asNumber
    // ends up being 1.2.
    const numberEqualsString = sanitizedNumber.split(separator).length < 3;
    if (
      field.type === FieldDataType.decimal &&
      (!isNumber || !numberEqualsString)
    ) {
      this.notify.error('"' + value + '" cannot be parsed into a decimal.');
      return '';
    }

    return field.type === FieldDataType.decimal ||
      field.type === FieldDataType.integer
      ? sanitizedNumber
      : value;
  }
}
